import classNames from 'classnames';
import { Formik } from 'formik';
import { IsotopicDistribution, MF } from 'mass-tools';
import queryString from 'query-string';
import React, { useState, useEffect } from 'react';
import { Form, Modal } from 'react-bootstrap';
import ReactCardFlip from 'react-card-flip';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useHistory } from 'react-router-dom';

import useChartSettings from '../../hooks/useChartSettings';
import ChartSettingsForm from '../Forms/ChartSettingsForm';
import { NewsCard } from '../News';
import CSVDownloader from '../utils/CSVDownloader';
import CSVTextDisplay from '../utils/CSVTextDisplay';
import ClipboardCopy from '../utils/ClipboardCopy';
import InputField from '../utils/InputField';
import IsotopicDistributionChart from '../utils/IsotopicDistributionChart';
import Meta from '../utils/Meta';

import ResultAnalysis from './ResultAnalysis';

function MolecularFormula() {
  const [margin, setMargin] = useState('70px');

  const [helpModalVisibility, setHelpModalVisibility] = useState(false);

  const [gaussianOptions, setGaussianOptions] = useState({});
  const [ionizations, setIonizations] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [chartSettings, _] = useChartSettings();

  const [molecularFormulaString, setMolecularFormulaString] = useState('');
  const [mf, setMf] = useState('');
  const [centroidData, setCentroidData] = useState([]);

  const [showResults, setResultsVisibility] = useState(false);
  const [showAdvancedSettings, setAdvancedSettingVisibility] = useState(false);

  const [isFlipped, setIsFlipped] = useState(false);
  const [isCsvTextDisplayed, setCsvTextVisibility] = useState(false);

  const history = useHistory();
  const fullscreenHandle = useFullScreenHandle();

  useEffect(() => {
    if (fullscreenHandle.active) {
      setIsFlipped(false);
    }
  }, [fullscreenHandle.active]);

  useEffect(() => {
    const queries = queryString.parse(history.location.search, {
      parseNumbers: true,
    });

    if (queries.mf) {
      setMolecularFormulaString(queries.mf);
      setResultsVisibility(true);
    }

    if (queries.ionizations) {
      setIonizations(queries.ionizations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (molecularFormulaString) {
      setMargin('0px');
      setResultsVisibility(true);
    } else {
      setMargin('70px');
      setResultsVisibility(false);
    }
  }, [molecularFormulaString]);

  useEffect(() => {
    if (!molecularFormulaString) return;

    let mfString = molecularFormulaString;
    if (mfString === 'throw') {
      throw new Error('Test throw error');
    }
    try {
      new MF(mfString, { ensureCase: true }).getInfo();
    } catch (e) {
      mfString = '';
    }

    let options = {
      ionizations: mfString !== '' ? ionizations : '',
      maxLines: 1000,
      allowNeutral: true,
    };

    options = JSON.parse(JSON.stringify(options));
    options.ensureCase = true;

    setMf(mfString);
    setGaussianOptions(options);

    let tableData = [];

    try {
      const isotopicDistribution = new IsotopicDistribution(mfString, {
        ...options,
        fwhm: chartSettings.fwhm,
      });
      tableData = isotopicDistribution.getTable({
        xLabel: 'mz',
        yLabel: 'intensity',
        maxValue: 100,
      });
    } catch (e) {
      //
    }
    setCentroidData(tableData);
  }, [molecularFormulaString, ionizations, chartSettings]);

  function handleShow() {
    setHelpModalVisibility(true);
  }

  function handleClose() {
    setHelpModalVisibility(false);
  }

  function handleClick() {
    if (isFlipped) {
      setIsFlipped(!isFlipped);
    } else {
      setCsvTextVisibility(false);
      setIsFlipped(true);
    }
  }

  function displayText() {
    if (isFlipped) {
      setIsFlipped(!isFlipped);
    } else {
      setCsvTextVisibility(true);
      setIsFlipped(true);
    }
  }
  return (
    <div className="m-auto justify-center">
      <Meta
        description="Calculate molecular weight, monoistopic mass and isotopic distribution from a molecular formula."
        keywords="Monoisotopic mass, Molecular formula, MF"
        title="ChemCalc: molecular formula information"
        pageViewKey="mf"
      />
      <div>
        <div
          className="transactional_bloc mx-auto w-11/12 sm:w-4/5 md:w-2/3 lg:w-1/2"
          style={{ marginTop: margin }}
        >
          <div className="flex justify-center w-full">
            <h2 className="atom-heading">MF ANALYSIS</h2>
          </div>

          <Formik
            enableReinitialize={true}
            initialValues={{
              molecularFormula: molecularFormulaString,
              ionizations,
            }}
            onSubmit={(values, actions) => {
              actions.setSubmitting(false);
            }}
            validate={(values) => {
              setMolecularFormulaString(values.molecularFormula);
              setIonizations(values.ionizations);

              const search = queryString.parse(history.location.search);
              search.mf = values.molecularFormula;
              search.ionizations = values.ionizations;
              const searchString = queryString.stringify(search);
              history.push({ search: searchString });
              return false;
            }}
          >
            {(props) => (
              <Form className="m-auto" onSubmit={props.handleSubmit}>
                <Form.Group
                  controlId="formBasicPassword"
                  className="flex items-center"
                >
                  <InputField
                    name="molecularFormula"
                    type="text"
                    placeholder="Enter your Molecular Formula"
                    className="block w-full py-2 px-4 rounded-full outline-none focus:ring bg-white"
                  />

                  <i
                    className="fas fa-question-circle fa-2x ml-2 text-white hover:bg-black cursor-pointer rounded-full p-1"
                    onClick={handleShow}
                  />

                  <i
                    className={classNames(
                      'fas fa-cog fa-2x ml-2 text-white hover:bg-black cursor-pointer rounded-full p-1',
                      showAdvancedSettings && 'bg-black',
                    )}
                    onClick={() =>
                      setAdvancedSettingVisibility(!showAdvancedSettings)
                    }
                  />
                </Form.Group>
                {showAdvancedSettings && (
                  <Form.Group className="flex my-10 items-center">
                    <p className="m-0  text-white">Ionizations</p>

                    <InputField
                      className="outline-none focus:ring w-1/2 py-1 px-2 ml-2"
                      name="ionizations"
                      type="text"
                    />
                  </Form.Group>
                )}
              </Form>
            )}
          </Formik>
          {showResults ? null : <NewsCard />}
        </div>

        {showResults && (
          <div className="w-full m-auto flex flex-col sm:w-11/12 md:w-2/3 lg:w-4/5 lg:flex-row">
            <div className="mt-16 lg:w-1/2">
              <ResultAnalysis analysis={molecularFormulaString} />
            </div>

            <div className="mt-6 mx-2 lg:w-1/2">
              <div className="h-10 justify-end hidden sm:flex">
                <CSVDownloader
                  data={centroidData}
                  filename={molecularFormulaString}
                  className="mr-3 text-white"
                />
                <ClipboardCopy
                  data={centroidData}
                  mode={isCsvTextDisplayed ? 'csv' : 'img'}
                  className="mr-3 text-white"
                />
                <i
                  onClick={displayText}
                  className={classNames(
                    'fas fa-table fa-2x cursor-pointer mr-3',
                    isFlipped && isCsvTextDisplayed
                      ? 'text-black'
                      : 'text-white',
                  )}
                />
                <i
                  className={classNames(
                    'fas fa-chart-area fa-2x cursor-pointer mr-3',
                    isFlipped && !isCsvTextDisplayed
                      ? 'text-black'
                      : 'text-white',
                  )}
                  onClick={handleClick}
                />
                <i
                  className="fas fa-search-plus fa-2x cursor-pointer text-white"
                  onClick={fullscreenHandle.enter}
                />
              </div>
              <ReactCardFlip isFlipped={isFlipped} flipDirection="horizontal">
                <FullScreen handle={fullscreenHandle}>
                  <div
                    style={{
                      height: fullscreenHandle.active ? '100vh' : '320px',
                      backgroundColor: 'white',
                    }}
                  >
                    {!isFlipped && (
                      <IsotopicDistributionChart
                        mf={mf}
                        option={gaussianOptions}
                        style={{ height: '100%' }}
                      />
                    )}
                  </div>
                </FullScreen>

                <div
                  className=""
                  style={{
                    height: '320px',
                    background: '#fff',
                    padding: '5px',
                    display: 'grid',
                  }}
                >
                  {isCsvTextDisplayed ? (
                    <div className="csvText">
                      <CSVTextDisplay
                        data={[...centroidData]}
                        columnsMappers={{
                          mz: { toFixed: 5 },
                          intensity: { toFixed: 2 },
                        }}
                      />
                    </div>
                  ) : (
                    <div className="py-4 px-2">
                      <ChartSettingsForm />
                      <button
                        type="submit"
                        onClick={handleClick}
                        className="border-black m-auto block px-4 py-2 border-2 rounded-lg text-black font-medium uppercase text-lg hover:bg-black hover:text-white outline-none outline-none focus:ring"
                      >
                        Done
                      </button>
                    </div>
                  )}
                </div>
              </ReactCardFlip>
            </div>
          </div>
        )}
      </div>
      <Modal show={helpModalVisibility} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Userguide</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            Simply enter a molecular formula by respecting the uppercase and
            lowercase.
          </div>
          <div>
            Salts are allowed and should the different parts should be separated
            by &lsquo;.&lsquo;.
          </div>
          <div>
            Group abbreviation for phenyl (Ph), ethyl (Et), alanine diradical
            (Ala) and over 100 other groups can be used.
          </div>
          <div />
          If you enter the charge the tool will take into account the mass of
          the electron and will also calculate m/z. volutpat. volutpat.
          <div>
            Here are some examples of molecular formula:
            <ul className="pl-4 list-disc">
              <li>C2H5OH</li>
              <li>[13C]2[2H]3[35Cl]: specify isotopes even non stable ones</li>
              <li>Et3N: use groups like Et, Ph, ...</li>
              <li>NO3(--), Na(+), SO4(2-): specify the charge</li>
              <li>HAlaGlyProOH: a tripeptide</li>
              <li>
                C{'{'}80:20{'}'}10: 10 carbons that has isotopic composition
                80:20
              </li>
            </ul>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default MolecularFormula;
